import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGetPromptsQuery, useGetPromptQuery } from "@/store/admin_api";

/**
 * Reusable picker for choosing a shared-library prompt + version. Used to assign a prompt version to
 * both an agent and a template. Presentational — the caller supplies `onAssign` (which persists) and
 * an `inheritLabel`/`assignedLabel` describing the current state.
 */
export default function PromptVersionPicker({
  currentVersionId,
  currentVersionLabel,
  onAssign,
  isSaving,
  inheritLabel,
  assignedLabel,
  allowReset = true,
}: {
  currentVersionId?: string | null;
  /** Resolved display name of the assigned version, served alongside the id by the backend. */
  currentVersionLabel?: string | null;
  onAssign: (promptVersionId: string | null, versionLabel?: string) => void;
  isSaving?: boolean;
  inheritLabel: string;
  assignedLabel: string;
  allowReset?: boolean;
}) {
  const { data: promptPage } = useGetPromptsQuery({ size: 200 });
  const prompts = promptPage?.items;
  const [selectedPromptId, setSelectedPromptId] = useState("");
  const { data: promptDetail } = useGetPromptQuery(selectedPromptId, { skip: !selectedPromptId });
  const [selectedVersionId, setSelectedVersionId] = useState("");

  const handleAssign = () => {
    const selectedVersion = promptDetail?.versions?.find((version) => version.id === selectedVersionId);
    const versionLabel = selectedVersion
      ? [promptDetail?.name, `v${selectedVersion.version}`, selectedVersion.label].filter(Boolean).join(" · ")
      : undefined;
    onAssign(selectedVersionId, versionLabel);
  };

  return (
    <div className="space-y-3">
      {/* The dropdowns below are "pick a new one" controls and always start empty, so the currently
          assigned version is shown here explicitly — otherwise a saved assignment looks lost. */}
      <div className="rounded-md border bg-background px-3 py-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Currently assigned</p>
        <p className="text-sm font-medium text-foreground">
          {currentVersionId
            ? currentVersionLabel || "Assigned (prompt unavailable)"
            : "None — inheriting"}
        </p>
      </div>
      <p className="text-xs text-muted-foreground">{currentVersionId ? assignedLabel : inheritLabel}</p>
      <div className="flex flex-wrap items-end gap-2 max-w-2xl">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Prompt</label>
          <Select value={selectedPromptId} onValueChange={(v) => { setSelectedPromptId(v); setSelectedVersionId(""); }}>
            <SelectTrigger className="w-[220px]"><SelectValue placeholder="Choose a prompt" /></SelectTrigger>
            <SelectContent>
              {(prompts ?? []).map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Version</label>
          <Select value={selectedVersionId} onValueChange={setSelectedVersionId} disabled={!selectedPromptId}>
            <SelectTrigger className="w-[220px]"><SelectValue placeholder="Choose a version" /></SelectTrigger>
            <SelectContent>
              {(promptDetail?.versions ?? [])
                .filter((v) => v.status !== "ARCHIVED")
                .map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    v{v.version}{v.label ? ` · ${v.label}` : ""}{v.status === "DRAFT" ? " (draft)" : ""}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" size="sm" disabled={!selectedVersionId || isSaving} onClick={handleAssign}>
          Assign
        </Button>
        {allowReset && currentVersionId && (
          <Button size="sm" variant="outline" disabled={isSaving} onClick={() => onAssign(null)}>
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}
